package com.schoolconnect.app.datasource.network.response

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class StaffReportPaginationResponse(
    val count: Int? = null,
    val limit: Int? = null,
    val offset: Int? = null
)

@Serializable
data class AttendanceReportSummaryResponse(
    @SerialName("total_students") val totalStudents: Int? = null,
    @SerialName("working_days") val workingDays: Int? = null,
    @SerialName("overall_attendance_percentage") val overallAttendancePercentage: Double? = null,
    @SerialName("matching_records_count") val matchingRecordsCount: Int? = null,
    @SerialName("matching_records_label") val matchingRecordsLabel: String? = null
)

@Serializable
data class AttendanceReportRowResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    val students: Int? = null,
    @SerialName("working_days") val workingDays: Int? = null,
    @SerialName("present_days") val presentDays: Double? = null,
    @SerialName("absent_days") val absentDays: Double? = null,
    @SerialName("absent_morning") val absentMorning: Double? = null,
    @SerialName("absent_afternoon") val absentAfternoon: Double? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("attendance_percentage") val attendancePercentage: Double? = null,
    val status: String? = null
)

@Serializable
data class AttendanceReportResponse(
    val summary: AttendanceReportSummaryResponse? = null,
    @SerialName("report_preview") val reportPreview: List<AttendanceReportRowResponse>? = null,
    val pagination: StaffReportPaginationResponse? = null
)

@Serializable
data class AcademicPerformanceSummaryResponse(
    @SerialName("total_students") val totalStudents: Int? = null,
    @SerialName("total_exams") val totalExams: Int? = null,
    @SerialName("overall_exam_average") val overallExamAverage: Double? = null,
    @SerialName("category_summary") val categorySummary: List<AcademicPerformanceCategorySummaryResponse>? = null,
    @SerialName("matching_records_count") val matchingRecordsCount: Int? = null,
    @SerialName("matching_records_label") val matchingRecordsLabel: String? = null
)

@Serializable
data class AcademicPerformanceCategorySummaryResponse(
    @SerialName("category_name") val categoryName: String? = null,
    @SerialName("range_label") val rangeLabel: String? = null,
    @SerialName("student_count") val studentCount: Int? = null
)

@Serializable
data class AcademicPerformanceReportRowResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    val students: Int? = null,
    @SerialName("exams_count") val examsCount: Int? = null,
    @SerialName("exam_average_percentage") val examAveragePercentage: Double? = null,
    @SerialName("category_counts") val categoryCounts: Map<String, Int>? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("marks_obtained") val marksObtained: Double? = null,
    @SerialName("max_marks") val maxMarks: Double? = null,
    @SerialName("exam_percentage") val examPercentage: Double? = null,
    @SerialName("performance_category") val performanceCategory: String? = null,
    val rank: Int? = null
)

@Serializable
data class AcademicPerformanceReportResponse(
    val summary: AcademicPerformanceSummaryResponse? = null,
    @SerialName("report_preview") val reportPreview: List<AcademicPerformanceReportRowResponse>? = null,
    val pagination: StaffReportPaginationResponse? = null
)

@Serializable
data class CombinedPerformanceSummaryResponse(
    @SerialName("total_students") val totalStudents: Int? = null,
    @SerialName("overall_attendance_percentage") val overallAttendancePercentage: Double? = null,
    @SerialName("overall_exam_average") val overallExamAverage: Double? = null,
    @SerialName("matching_records_count") val matchingRecordsCount: Int? = null,
    @SerialName("matching_records_label") val matchingRecordsLabel: String? = null
)

@Serializable
data class CombinedPerformanceReportRowResponse(
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    val students: Int? = null,
    @SerialName("attendance_percentage") val attendancePercentage: Double? = null,
    @SerialName("attendance_category") val attendanceCategory: String? = null,
    @SerialName("exam_average_percentage") val examAveragePercentage: Double? = null,
    @SerialName("exam_category") val examCategory: String? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("present_days") val presentDays: Double? = null,
    @SerialName("absent_days") val absentDays: Double? = null,
    @SerialName("absent_morning") val absentMorning: Double? = null,
    @SerialName("absent_afternoon") val absentAfternoon: Double? = null,
    @SerialName("working_days") val workingDays: Int? = null,
    @SerialName("exams_count") val examsCount: Int? = null,
    @SerialName("total_marks_obtained") val totalMarksObtained: Double? = null,
    @SerialName("total_max_marks") val totalMaxMarks: Double? = null
)

@Serializable
data class CombinedPerformanceReportResponse(
    val summary: CombinedPerformanceSummaryResponse? = null,
    @SerialName("report_preview") val reportPreview: List<CombinedPerformanceReportRowResponse>? = null,
    val pagination: StaffReportPaginationResponse? = null
)
